import APIService from "@/services/api.service";
import authHeader from "./auth-header";
import store from '@/store/Index';
import moment from "moment";
import i18n from '@/plugins/i18n';

class AppService {

  // CRM Produkt Id's
  Starter = 1;
  Premium = 3;
  Enterprise = 15;
  PremiumAdopt = 16;

  getProducts(key) {
    const products = {
      Starter: [this.Starter, this.Premium, this.Enterprise, this.PremiumAdopt],
      Premium: [this.Premium, this.PremiumAdopt, this.Enterprise],
      Enterprise: [this.Enterprise],
    };
    return products[key];
  }

  isComponentExtended(component) {
    const components = this.getFromLocalStorage("components");
    let extended = false;
    components.forEach((item) => {
      if (item === component) {
        extended = true;
      }
    });
    return extended;
  }

  extendCoreComponents(components) {
    const existingComponents = JSON.parse(localStorage.getItem("components")) || [];
    components.forEach((component) => {
      if (!existingComponents.includes(component)) {
        existingComponents.push(component);
      }
    });
    localStorage.setItem("components", JSON.stringify(existingComponents));
  }

  getFromLocalStorage(key) {
    let result = [];
    const data = JSON.parse(localStorage.getItem(key));
    if (data) {
      return data;
    }
    return result;
  }

  runWarnHandler(app) {
    app.config.warnHandler = (msg, instance, trace) => {
      console.warn(`Meine Vue Warungen \n: ${msg}`, instance, trace);
    };
  }

  log(string, input) {
    console.log(string, input);
  }

  addLinks(app, elements) {
    if (app.menuItems) {
      elements.forEach((route) => {
        if (route.index > 0) {
          app.menuItems.splice(route.index, 0, {
            url: route.path,
            text: (route.meta && route.meta.label) || route.name,
            icon: route.icon,
            product: route.meta.product,
          });
        }
      });
    }
  }

  addContentLinks(app, elements) {
    if (app.contentMenuItems) {
      elements.forEach((route) => {
        if (route.index > 0) {
          app.contentMenuItems.splice(route.index, 0, {
            url: route.path,
            text: (route.meta && route.meta.label) || route.name,
            icon: route.icon,
            product: route.meta.product,
          });
        }
      });
    }
  }

  addConsumerLinks(app, elements) {
    if (app.consumerLinks) {
      elements.forEach((route) => {
        if (
          route.path.length > 0 &&
          !app.consumerLinks.some((link) => link.url === route.path)
        ) {
          app.consumerLinks.push({
            text: (route.meta && route.meta.label) || route.name,
            icon: route.icon,
            url: route.path,
            product: route.meta.product,
          });
        }
      });
    }
  }

  addComponents(router) {
    const routes = this.getFromLocalStorage("routes");
    routes.forEach((route) => {
      this.addAdminRoute(router, route);
    });
  }

  addRoutes(app, elements) {
    //we found the app
    if (app._uid === 3) {
      const routes = this.getFromLocalStorage("routes");
      elements.forEach((route) => {
        this.addAdminRoute(app.$router, route);
        if (!routes.some((item) => item.path === route.path)) {
          routes.push(route);
        }
      });
      localStorage.setItem("routes", JSON.stringify(routes));
    }
  }

  async ensureLicenceLoaded() {
    try {
      if (!store.state.main.config?.result) {
        await store.dispatch('fetch', {
          endpoint: '/api/v1/public',
          state: 'config',
          header: false
        });
      }

      const licenceId = store.state.main.config?.result?.licence;

      if (licenceId && !store.state.main.licence?.result) {
        await store.dispatch('fetch', {
          endpoint: `/api/v1/licences/${licenceId}`,
          state: 'licence',
          host: 'getAuthHost'
        });
      }
    } catch (error) {
      console.error('Error loading licence:', error);
      throw error;
    }
  }


  addAdminRoute(router, route) {
    // Prüfe ob Route bereits existiert
    const existingRoute = router.getRoutes().find(r => r.path === route.path);
    if (existingRoute) {
      return; // Route bereits vorhanden, nicht nochmal hinzufügen
    }

    const routeConfig = {
      path: route.path,
      name: route.name,
      meta: route.meta,
      component: () => import(`../../plugins/${route.component}`),
    };

    // Guard nur, wenn roles definiert sind.
    if (route.meta.roles) {
      routeConfig.beforeEnter = router.guard;
    }

    router.addRoute(routeConfig);
  }

  hasProduct(products) {
    return products.includes(store.state.main?.licence?.result?.product?.crm_id);
  }

  // Consumer Subscription Active?
  // Sub 4 Content inside the App.
  // Access needs one usable subscription. See isSubscriptionUsable().
  isConsumerSubActive() {
    const subs = store.state.main.user?.result?.subscriptions;
    if (!subs || !subs.length) {
      return false;
    }
    return subs.some((sub) => this.isSubscriptionUsable(sub));
  }

  // Creator Subscription Active?
  // Sub 4 App
  isCreatorSubActive() {
    return store.state.main.licence?.result?.subscription;
  }

  // Platform has explicit content
  isExplicit() {
    if (typeof store.state.main.config.result !== "undefined") {
      return store.state.main.config.result.explicit;
    }
    return;
  }


  // Creator is Tester (7 Days)
  isTester() {
    if (Object.keys(store.state.main.licence).length > 0) {
      const lic = store.state.main.licence.result;
      const daysLeft = this.howMuchDaysLeft(lic.created);
      const timeRange = 30;

      if (daysLeft <= timeRange) {
        // is Tester
        return true;
      } else {
        // isn´t Tester
        return false;
      }
    }
  }

  isPaid(item, items = "images") {
    if (!store.state.main.user.result) {
      return false;
    }

    const paidOrders = store.state.main.user.result.orders;
    if (!paidOrders || !paidOrders.length) {
      return false;
    }

    if (items === "gallery") {
      return item.images.some((image) =>
        paidOrders.some((order) =>
          order.images.some((orderImage) => orderImage.id === image.id)
        )
      );
    }

    if (items === "images") {
      return paidOrders.some((order) =>
        order[items].some((image) => image.id === item.id)
      );
    }

    if (items === "videos") {
      return paidOrders.some((order) =>
        order[items].some((video) => video.id === item.id)
      );
    }
    // If none of the conditions match, return false
    return false;
  }

  // Rule for one subscription row (Key rule, Kevin and Konstantin 2026-10-01).
  // Access needs:
  //   (status == 'active' and no cancelled_at)
  //   or (status 'active' or 'cancelled', cancelled_at set and a next billing
  //       date in the future).
  // So a cancelled row keeps the access until the end of the paid period.
  // A cancelled row without a next billing date has no access.
  // A suspended, pending or past_due row never grants access.
  isSubscriptionUsable(sub) {
    if (!sub || (sub.status !== 'active' && sub.status !== 'cancelled')) {
      return false;
    }

    // A running subscription without a cancellation keeps the access.
    if (sub.status === 'active' && !sub.cancelled_at) {
      return true;
    }

    // A cancelled row keeps the access only until the next billing date.
    if (!sub.cancelled_at || !sub.next_billing_date) {
      return false;
    }

    const billing = new Date(sub.next_billing_date);
    const billingDay = new Date(
      billing.getFullYear(),
      billing.getMonth(),
      billing.getDate()
    );
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return billingDay > today;
  }

  isSubscription(item) {
    if (!store.state.main.user.result) {
      return false;
    }

    const subs = store.state.main.user.result.subscriptions;
    if (!subs || !subs.length) {
      return false;
    }

    return subs.some((sub) => {
      if (!this.isSubscriptionUsable(sub)) {
        return false;
      }
      return item.plans.some((plan) => plan.id === sub.plan.id);
    });
  }

  isAgeVerified() {
    const age_verified = store.state.main.auth_user?.result?.age_verified
    if (age_verified == null || age_verified == false) {
      return false;
    }
    return true;
  }

  canAccess(item, type = "images") {
    const hasPaymentAccess = this.hasPaymentAccess(item, type);

    if (!hasPaymentAccess) return false;

    if (item.fsk === 18) {
      return this.isAgeVerified();
    }

    return true;
  }

  hasPaymentAccess(item, type) {
    const isPaid = item.price > 0 && this.isPaid(item, type);
    const isSub = item.subscription && this.isSubscription(item);

    if (item.price > 0 || item.subscription) {
      return isPaid || isSub;
    }

    return true;
  }

  canAccessGallery(gallery) {
    return this.canAccess(gallery, "gallery");
  }

  canAccessImage(image) {
    return this.canAccess(image, "images");
  }

  canAccessVideo(video) {
    return this.canAccess(video, "videos");
  }

  checkVideoAccess(video) {
    if (video.price > 0) {
      return this.isPaid(video, "videos");
    }
    if (video.subscription) {
      return this.isSubscription(video);
    }
    return true;
  }

  // Darf dieses Video abgespielt werden? Regeln wie im Backend
  // (Shop-VideoController::key): geteilte Videos und der Betreiber
  // (ROLE_TENANT) sind frei, sonst entscheidet der Kauf oder ein Abo.
  // Vor dem Key-Abruf und vor dem Player-Start pruefen.
  canPlayVideo(video) {
    if (!video) {
      return false;
    }

    if (video.share) {
      return true;
    }

    if (this.hasRole(['ROLE_TENANT'])) {
      return true;
    }

    if (!store.state.auth.status.loggedIn) {
      return false;
    }

    return this.checkVideoAccess(video);
  }

  checkImageAccess(image) {
    if (image.price > 0) {
      return this.isPaid(image, "images");
    }
    if (image.subscription) {
      return this.isSubscription(image);
    }
    return true;
  }

  formatPlans(object, state) {
    // Plans formatieren
    if (object.hasOwnProperty("plans") && object.plans) {
      if (typeof object.plans === 'string' && object.plans.trim() !== '') {
        const numbersArray = object.plans.trim().split(",").map(Number);
        object.plans = numbersArray.filter(num => !isNaN(num));
      } else if (Array.isArray(object.plans)) {
        object.plans = object.plans.map(p => typeof p === 'object' ? p.id : p);
      } else {
        object.plans = [];
      }
    }
    return object;
  }

  getTimeElapsed(dateTimeString) {
    const dateTime = new Date(dateTimeString);
    const now = new Date();
    const timeDiffMs = now.getTime() - dateTime.getTime();

    const secondsElapsed = Math.floor(timeDiffMs / 1000);
    const minutesElapsed = Math.floor(secondsElapsed / 60);
    const hoursElapsed = Math.floor(minutesElapsed / 60);
    const daysElapsed = Math.floor(hoursElapsed / 24);
    const monthsElapsed = Math.floor(daysElapsed / 30);
    const yearsElapsed = Math.floor(monthsElapsed / 12);

    if (secondsElapsed < 60) {
      return i18n.tc('time.ago_seconds', secondsElapsed, { n: secondsElapsed });
    } else if (minutesElapsed < 60) {
      return i18n.tc('time.ago_minutes', minutesElapsed, { n: minutesElapsed });
    } else if (hoursElapsed < 24) {
      return i18n.tc('time.ago_hours', hoursElapsed, { n: hoursElapsed });
    } else if (daysElapsed < 30) {
      return i18n.tc('time.ago_days', daysElapsed, { n: daysElapsed });
    } else if (monthsElapsed < 12) {
      return i18n.tc('time.ago_months', monthsElapsed, { n: monthsElapsed });
    } else {
      return i18n.tc('time.ago_years', yearsElapsed, { n: yearsElapsed });
    }
  }

  getNextBillingDate(transactionDate, recurringStatus, recurringPeriod) {
    // Return null if not recurring
    if (recurringStatus !== "y") {
      return i18n.t('subscription.ends_soon');
    }

    const startDate = moment(transactionDate);
    const today = moment();
    const periodInDays = parseInt(recurringPeriod);

    // Calculate how many periods have passed
    const daysSinceStart = today.diff(startDate, "days");
    const periodsElapsed = Math.floor(daysSinceStart / periodInDays);

    // Calculate next billing date by adding one more period
    const nextBillingDate = startDate
      .clone()
      .add((periodsElapsed + 1) * periodInDays, "days");

    return nextBillingDate.format("YYYY-MM-DD");
  }

  howMuchDaysLeft(startDate) {
    // Startdatum in Date-Objekt konvertieren
    const startDateObj = new Date(startDate);
    // Heutiges Datum
    const today = new Date();
    // Anzahl der verbleibenden Tage berechnen
    // Calculate the number of days left in the subscription
    const daysLeft = Math.ceil((today - startDateObj) / (1000 * 60 * 60 * 24));

    // var dayCount = (100/timeRange) * daysLeft
    return daysLeft;
  }

  add30Days(startDate) {
    var startDateUTC = this.formUTCDate(startDate, "YYYY-DD-MM"); // Adjust the format if needed
    if (!startDateUTC) {
      throw new Error(i18n.t('validation.invalid_date'));
    }

    const newDate = moment
      .utc(startDateUTC, "YYYY-DD-MM")
      .add(30, "days")
      .toDate();
    return this.formDate(newDate);
  }

  formUTCDate(value, format) {
    if (value) {
      return moment.utc(String(value)).format(format);
    }
  }

  formDate(value) {
    if (value) {
      return moment(String(value)).format("DD.MM.YYYY");
    }
  }

  isOwner(user) {
    return user.id == store.state.main.user.result.id;
  }

  hasRole(roles) {
    if (Object.keys(store.state.main.user).length > 0) {
      const user = store.state.main.user.result;
      if (roles.includes(user.role)) {
        return true;
      }
    }
    return false;
  }

  getFirstLetter(str) {
    const firstLetter = str.charAt(0);
    return firstLetter;
  }

  mouseOverCard(wrapperClass, cardClass) {
    const wrapper = document.querySelector(`${wrapperClass}`);
    const cards = document.querySelectorAll(`${cardClass}`);

    // console.log("wrapper", wrapper)
    // console.log("listener",wrapper.getAttribute('listener'))

    if (wrapper.getAttribute("listener") !== "true") {
      // console.log("wrapperClass", wrapperClass);
      wrapper.addEventListener("mousemove", function (e) {
        cards.forEach((card) => {
          const rect = card.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;

          card.style.setProperty("--xPos", `${x}px`);
          card.style.setProperty("--yPos", `${y}px`);
        });
      });
    }
  }

  imageSource(path, width, height) {
    let src = require("@/assets/logo/LogoMark_blue_new.png");
    let size = "";

    if (path != null) {
      if (typeof height !== "undefined") {
        size = `width=${width}&height=${height - 0.1}`;
      }
      src = APIService.getApiHost() + `/${path}?${size}`;
    }
    return src;
  }

  imageSourceWithToken(path, width, height) {
    let src = require("@/assets/logo/LogoMark_blue_new.png");
    let size = "";
    const token = authHeader("access_token").Authorization;

    if (path != null) {
      if (typeof height !== "undefined") {
        size = `width=${width}&height=${height - 0.1}`;
      }
      src = APIService.getApiHost() + `/${path}?${size}&token=${token}`;
    }
    return src;
  }

  moveItem(array, fromIndex, toIndex) {
    var element = array[fromIndex];
    array.splice(fromIndex, 1);
    array.splice(toIndex, 0, element);
  }

  indexAfter(videos, index) {
    index++;
    if (index >= videos.length) {
      index = 0;
    }
    return index;
  }

  indexBefore(videos, index) {
    index--;
    if (index < 0) {
      index = videos.length - 1;
    }
    return index;
  }

  addAfter(comp, items, index) {
    const oldIndex = index;
    const newindex = this.indexAfter(items, index);

    comp.savePosition(items[oldIndex].id, items[newindex].id).then(() => {
      this.moveItem(items, oldIndex, newindex);
    });
  }

  addBefore(comp, items, index) {
    const oldIndex = index;
    const newindex = this.indexBefore(items, index);

    comp.savePosition(items[oldIndex].id, items[newindex].id).then(() => {
      this.moveItem(items, oldIndex, newindex);
    });
  }

  getDurationAsString(mins) {
    const duration = mins.split(":");
    const hours = Math.floor(duration[0]);
    let seconds = Math.round(duration[2]);

    seconds = seconds < 10 ? "0" + seconds : seconds;

    if (hours > 0) {
      return `${hours}:${duration[1]}:${seconds}`;
    }
    return `${Math.floor(duration[1])}:${seconds}`;
  }

  linkTo(route, router, query) {
    // A content page hands the item over as a query (rule of Kevin 2026-10-02).
    const target = query ? { path: "/" + route, query } : "/" + route;

    router.push(target).catch((error) => {
      APIService.setResponse({
        message: i18n.t('router.already_here'),
        color: "blue",
      });
    });
  }

  // acceptCookieBanner() {
  //   this.setCookie('cookieBanner', 'true');
  // }

  setCookie(name, value) {
    let cookie = name + "=" + encodeURIComponent(value);
    document.cookie = cookie;
  }

  getCookie(name) {
    let cookieArr = document.cookie.split(";");
    for (let i = 0; i < cookieArr.length; i++) {
      let cookiePair = cookieArr[i].split("=");
      if (name == cookiePair[0].trim()) {
        return decodeURIComponent(cookiePair[1]);
      }
    }
    return null;
  }

}
export default new AppService();
